import { format } from 'date-fns'
import { CoverageMode } from '@/Network/Types/Requests/CoverageRequests'
import { CoverageStatus } from '@/Network/Types/Responses/CoverageResponses'
import type { CoverageBlockedReason } from '@/Network/Types/Responses/CoverageResponses'
import { DoctorStationPackageAuditType } from '@/Network/Types/Requests/StationRequests'
import type {
  PatchStationsFromSchedulePayload,
  PatchStationsRequest,
  StationTypes
} from '@/Network/Types/Requests/StationRequests'
import type { StationFormSchedule } from '@/Network/Types/Responses/StationResponses'
import {
  COVERAGE_ACTION_LABELS,
  hasCoverageContract,
  resolveCoverageStatus
} from '@/utils/coverage'

/**
 * M4-B / M6-B / M7-B — o "plano" de um lote de plantões, puro.
 * Nenhuma decisão de ação, contagem ou payload nasce nos templates dos modais.
 */
export type CoverageRowAction =
  | 'CREATE'
  | 'TRANSFER'
  | 'UNIDENTIFIED'
  | 'REMOVE'
  | 'IGNORED'
  | 'NONE'
export type CoveragePlanScope = 'all' | 'doctorOnly' | 'unidentifiedOnly'

export interface CoveragePlanRow {
  station: StationFormSchedule
  status: CoverageStatus
  blockedReason: CoverageBlockedReason | null
  action: CoverageRowAction
}

export interface CoveragePlanCounts {
  OPEN: number
  DOCTOR: number
  UNIDENTIFIED: number
  BLOCKED: number
}

export interface CoveragePlan {
  target: CoverageMode
  hasContract: boolean
  rows: CoveragePlanRow[]
  applicableRows: CoveragePlanRow[]
  counts: CoveragePlanCounts
  notifiedDoctorNames: string[]
}

export type BulkOperation = PatchStationsFromSchedulePayload['operations'][number]

export const BLOCKED_REASON_LABELS: Record<CoverageBlockedReason, string> = {
  ANTECIPE: 'Antecipe solicitado ou pago',
  CHECKIN: 'check-in já realizado',
  POST_STATION_APPROVED: 'pós-plantão aprovado'
}

/** Motivo curto do "Ignorado" na coluna AÇÃO da pré-visualização. */
export const BLOCKED_REASON_SHORT_LABELS: Record<CoverageBlockedReason, string> = {
  ANTECIPE: 'Antecipe',
  CHECKIN: 'Check-in',
  POST_STATION_APPROVED: 'Pós-plantão aprovado'
}

export const AVAILABILITY_LABELS = {
  OPEN: 'Aberta',
  DOCTOR: 'Com médico',
  UNIDENTIFIED: 'Ocupado s/ ident.',
  BLOCKED: 'Bloqueado',
  CONFLICT: 'Conflitante'
} as const

/** Cor do chip da ação = estado de DESTINO do plantão. */
export type CoverageActionTone = 'doctor' | 'unidentified' | 'open' | 'blocked' | 'none'

const ACTION_TONES: Record<CoverageRowAction, CoverageActionTone> = {
  CREATE: 'doctor',
  TRANSFER: 'doctor',
  UNIDENTIFIED: 'unidentified',
  REMOVE: 'open',
  IGNORED: 'blocked',
  NONE: 'none'
}

export const resolveActionTone = (action: CoverageRowAction): CoverageActionTone =>
  ACTION_TONES[action]

/** Rótulo em pt-BR da ação; a origem ocupado s/ ident. refina CREATE/UNIDENTIFIED/REMOVE. */
export function resolveActionLabel(action: CoverageRowAction, status: CoverageStatus): string {
  const fromUnidentified = status === CoverageStatus.UNIDENTIFIED
  switch (action) {
    case 'CREATE':
      return fromUnidentified
        ? COVERAGE_ACTION_LABELS.IDENTIFY_DOCTOR
        : COVERAGE_ACTION_LABELS.ALLOCATE_DOCTOR
    case 'TRANSFER':
      return COVERAGE_ACTION_LABELS.TRANSFER_DOCTOR
    case 'UNIDENTIFIED':
      return fromUnidentified
        ? COVERAGE_ACTION_LABELS.CHANGE_RESPONSIBLE
        : COVERAGE_ACTION_LABELS.MARK_UNIDENTIFIED
    case 'REMOVE':
      return fromUnidentified
        ? COVERAGE_ACTION_LABELS.RELEASE_OPEN
        : COVERAGE_ACTION_LABELS.REMOVE_DOCTOR
    case 'IGNORED':
      return COVERAGE_ACTION_LABELS.IGNORED
    default:
      return COVERAGE_ACTION_LABELS.NONE
  }
}

const REASON_BY_ACTION: Partial<Record<CoverageRowAction, DoctorStationPackageAuditType>> = {
  CREATE: DoctorStationPackageAuditType.CREATE,
  TRANSFER: DoctorStationPackageAuditType.TRANSFER,
  UNIDENTIFIED: DoctorStationPackageAuditType.UNIDENTIFIED,
  REMOVE: DoctorStationPackageAuditType.REMOVE
}

export const coverageTableHeaders = [
  { key: 'id', label: 'ID' },
  { key: 'startDate', label: 'Data' },
  { key: 'startTime', label: 'Horário' },
  { key: 'availability', label: 'Situação atual' },
  { key: 'current', label: 'Atual' },
  { key: 'action', label: 'Ação' },
  { key: 'value', label: 'Valor' }
]

const plural = (count: number, singular: string, pluralForm: string) =>
  `${count} ${count === 1 ? singular : pluralForm}`

const formatDay = (iso: string) => format(new Date(iso), 'dd/MM')

export function resolveBlockedReason(
  station: StationFormSchedule,
  target: CoverageMode
): CoverageBlockedReason | null {
  const reason = station.blockedReason ?? null
  if (!reason) return null
  if (reason === 'ANTECIPE') return target === CoverageMode.UNIDENTIFIED ? reason : null
  return reason
}

export function resolveRowAction(
  status: CoverageStatus,
  target: CoverageMode,
  scope: CoveragePlanScope = 'all'
): CoverageRowAction {
  if (scope === 'doctorOnly' && status !== CoverageStatus.DOCTOR) return 'NONE'
  if (scope === 'unidentifiedOnly' && status !== CoverageStatus.UNIDENTIFIED) return 'NONE'
  if (target === CoverageMode.DOCTOR)
    return status === CoverageStatus.DOCTOR ? 'TRANSFER' : 'CREATE'
  if (target === CoverageMode.OPEN) return status === CoverageStatus.OPEN ? 'NONE' : 'REMOVE'
  return 'UNIDENTIFIED'
}

const NOTIFYING_ACTIONS: CoverageRowAction[] = ['TRANSFER', 'UNIDENTIFIED', 'REMOVE']

export function buildCoveragePlan(
  stations: StationFormSchedule[],
  target: CoverageMode,
  options: { scope?: CoveragePlanScope } = {}
): CoveragePlan {
  const scope = options.scope ?? 'all'
  const counts: CoveragePlanCounts = { OPEN: 0, DOCTOR: 0, UNIDENTIFIED: 0, BLOCKED: 0 }

  const rows = stations.map((station): CoveragePlanRow => {
    const status = resolveCoverageStatus(station)
    const blockedReason = resolveBlockedReason(station, target)
    if (blockedReason) counts.BLOCKED += 1
    else counts[status] += 1
    const action = blockedReason ? 'IGNORED' : resolveRowAction(status, target, scope)
    return { station, status, blockedReason, action }
  })

  const applicableRows = rows.filter((r) => r.action !== 'IGNORED' && r.action !== 'NONE')
  const notifiedDoctorNames = [
    ...new Set(
      applicableRows
        .filter((r) => r.status === CoverageStatus.DOCTOR && NOTIFYING_ACTIONS.includes(r.action))
        .map((r) => r.station.doctorName?.trim())
        .filter((name): name is string => !!name)
    )
  ]

  return {
    target,
    hasContract: stations.some((station) => hasCoverageContract(station)),
    rows,
    applicableRows,
    counts,
    notifiedDoctorNames
  }
}

export function buildCoverageOperations(
  plan: CoveragePlan,
  context: { fallbackOriginDoctorId: number | null; packageId: number | null }
): BulkOperation[] {
  const groups = new Map<string, BulkOperation>()
  for (const row of plan.applicableRows) {
    const reason = REASON_BY_ACTION[row.action] as DoctorStationPackageAuditType
    const doctorId = plan.hasContract
      ? (row.station.doctorId ?? null)
      : context.fallbackOriginDoctorId
    const key = `${reason}-${doctorId}-${context.packageId}`
    const group: BulkOperation = groups.get(key) ?? {
      reason,
      doctorId,
      packageId: context.packageId,
      stationIds: [],
      doctorStationIds: undefined
    }
    group.stationIds.push(row.station.stationId)
    if (row.station.doctorStationId) {
      group.doctorStationIds = [...(group.doctorStationIds ?? []), row.station.doctorStationId]
    }
    groups.set(key, group)
  }
  return [...groups.values()]
}

export interface CoveragePatchForm {
  target: CoverageMode
  hasContract: boolean
  doctorId: number | null
  ourTeam: boolean
  userSponsorId: number | null
  formationId: number | null
  stationType: StationTypes | null
  responsibleId: number | null
  note: string
}

export function buildCoveragePatchStations(form: CoveragePatchForm): PatchStationsRequest {
  if (form.target === CoverageMode.UNIDENTIFIED) {
    const note = form.note.trim()
    return {
      coverage: CoverageMode.UNIDENTIFIED,
      coverageResponsibleId: form.responsibleId ?? undefined,
      ...(note ? { coverageNote: note } : {}),
      doctorId: null,
      ourTeam: false,
      userSponsorId: null,
      dsPaymentDoctorFormationId: null,
      stationType: form.stationType
    }
  }
  if (form.target === CoverageMode.OPEN) {
    return {
      ...(form.hasContract ? { coverage: CoverageMode.OPEN } : {}),
      doctorId: null,
      dsPaymentDoctorFormationId: null,
      ourTeam: false,
      userSponsorId: null,
      stationType: null
    }
  }
  return {
    ...(form.hasContract ? { coverage: CoverageMode.DOCTOR } : {}),
    doctorId: form.doctorId,
    ourTeam: form.ourTeam,
    userSponsorId: form.userSponsorId,
    dsPaymentDoctorFormationId: form.formationId,
    stationType: form.stationType
  }
}

const countApplicable = (plan: CoveragePlan, status: CoverageStatus) =>
  plan.applicableRows.filter((r) => r.status === status).length

type DescribeCounts = { open: number; doctor: number; unidentified: number }
type DescribeEntry = [count: number, line: () => string]

// Uma linha por status aplicável, na ordem de exibição de cada alvo (M4-B/M6-B/M7-B).
function describeEntries(
  target: CoverageMode,
  { open, doctor, unidentified }: DescribeCounts,
  targetDoctorName: string
): DescribeEntry[] {
  if (target === CoverageMode.DOCTOR) {
    return [
      [
        open,
        () => `${plural(open, 'vaga aberta recebe', 'vagas abertas recebem')} ${targetDoctorName}.`
      ],
      [
        unidentified,
        () =>
          `${plural(unidentified, 'ocupado sem identificação passa', 'ocupados sem identificação passam')} a ter médico identificado. O responsável é limpo.`
      ],
      [
        doctor,
        () =>
          `${plural(doctor, 'plantão com médico é transferido', 'plantões com médico são transferidos')} para ${targetDoctorName}.`
      ]
    ]
  }
  if (target === CoverageMode.UNIDENTIFIED) {
    return [
      [
        open,
        () =>
          `${plural(open, 'vaga aberta passa', 'vagas abertas passam')} a ocupado sem identificação.`
      ],
      [
        doctor,
        () =>
          `${plural(doctor, 'plantão com médico passa', 'plantões com médico passam')} a ocupado sem identificação.`
      ],
      [
        unidentified,
        () =>
          `${plural(unidentified, 'ocupado sem identificação troca', 'ocupados sem identificação trocam')} de responsável.`
      ]
    ]
  }
  return [
    [
      doctor,
      () => `${plural(doctor, 'plantão com médico vira', 'plantões com médico viram')} vaga aberta.`
    ],
    [
      unidentified,
      () =>
        `${plural(unidentified, 'ocupado sem identificação vira', 'ocupados sem identificação viram')} vaga aberta. O responsável é limpo.`
    ]
  ]
}

function describeTargetLines(plan: CoveragePlan, targetDoctorName: string): string[] {
  const counts: DescribeCounts = {
    open: countApplicable(plan, CoverageStatus.OPEN),
    doctor: countApplicable(plan, CoverageStatus.DOCTOR),
    unidentified: countApplicable(plan, CoverageStatus.UNIDENTIFIED)
  }
  return describeEntries(plan.target, counts, targetDoctorName)
    .filter(([count]) => count > 0)
    .map(([, line]) => line())
}

export function describeCoveragePlan(
  plan: CoveragePlan,
  context: { targetDoctorName?: string | null } = {}
): string[] {
  const lines = describeTargetLines(plan, context.targetDoctorName || 'o médico selecionado')
  const names = plan.notifiedDoctorNames
  if (names.length)
    lines.push(`${names.join(', ')} ${names.length === 1 ? 'é notificado' : 'são notificados'}.`)

  const blocked = plan.rows.filter((r) => r.action === 'IGNORED')
  if (blocked.length) {
    const detail = blocked
      .map(
        (r) =>
          `${formatDay(r.station.startDate)}, ${BLOCKED_REASON_LABELS[r.blockedReason as CoverageBlockedReason]}`
      )
      .join('; ')
    lines.push(`${plural(blocked.length, 'ignorado', 'ignorados')}: ${detail}`)
  }
  return lines
}

export interface CoverageTransitionSummary {
  total: number
  losingDoctorCount: number
  notifiedDoctorNames: string[]
  canceledActivityRequests: number | null
  obsoletedPostStationEvaluations: number | null
  canceledHandoverInvites: number | null
  openBecomingUnidentified: number
  blocked: Array<{ date: string; reason: CoverageBlockedReason }>
}

/** Soma um campo numérico opcional do preview; `null` se nenhuma linha o trouxe. */
function sumOptional(
  rows: CoveragePlanRow[],
  pick: (s: StationFormSchedule) => number | undefined
): number | null {
  const values = rows.map((r) => pick(r.station)).filter((v): v is number => typeof v === 'number')
  return values.length ? values.reduce((a, b) => a + b, 0) : null
}

export function buildTransitionSummary(plan: CoveragePlan): CoverageTransitionSummary {
  const losing = plan.applicableRows.filter((r) => r.status === CoverageStatus.DOCTOR)
  return {
    total: plan.applicableRows.length,
    losingDoctorCount: losing.length,
    notifiedDoctorNames: plan.notifiedDoctorNames,
    canceledActivityRequests: sumOptional(losing, (s) => s.pendingActivityRequests),
    obsoletedPostStationEvaluations: sumOptional(losing, (s) =>
      typeof s.hasPostStationEvaluation === 'boolean'
        ? Number(s.hasPostStationEvaluation)
        : undefined
    ),
    canceledHandoverInvites: sumOptional(losing, (s) => s.pendingHandoverInvites),
    openBecomingUnidentified: plan.applicableRows.filter((r) => r.status === CoverageStatus.OPEN)
      .length,
    blocked: plan.rows
      .filter((r) => r.action === 'IGNORED')
      .map((r) => ({
        date: formatDay(r.station.startDate),
        reason: r.blockedReason as CoverageBlockedReason
      }))
  }
}

/** M6: confirmação obrigatória quando médico real vira "sem identificação" (ou quando tudo bloqueou). */
export function needsTransitionConfirmation(plan: CoveragePlan): boolean {
  if (plan.target !== CoverageMode.UNIDENTIFIED) return false
  const losing = plan.applicableRows.some((r) => r.status === CoverageStatus.DOCTOR)
  return losing || (plan.applicableRows.length === 0 && plan.counts.BLOCKED > 0)
}

export interface CoveragePreviewTableRow {
  id: string
  startDate: string
  startTime: string
  endDate: string
  endTime: string
  availability: string
  current: string
  action: CoverageRowAction
  actionLabel: string
  actionTone: CoverageActionTone
  actionNote: string
  value: string
}

const formatValue = (value: number | null) =>
  value ? `R$${value.toFixed(2).replace('.', ',')}` : 'indisponível'

// O rótulo já diz "Identificar médico"/"Trocar responsável"; a nota fica só com o motivo do ignorado.
const resolveActionNote = (row: CoveragePlanRow): string =>
  row.blockedReason ? BLOCKED_REASON_SHORT_LABELS[row.blockedReason] : ''

export function toPreviewTableRow(row: CoveragePlanRow): CoveragePreviewTableRow {
  const { station } = row
  const availability = row.blockedReason
    ? AVAILABILITY_LABELS.BLOCKED
    : station.hasDoctorConflict
      ? AVAILABILITY_LABELS.CONFLICT
      : AVAILABILITY_LABELS[row.status]
  return {
    id: `#${station.stationId}`,
    startDate: format(new Date(station.startDate), 'dd/MM/yyyy'),
    startTime: format(new Date(station.startTime), 'HH:mm'),
    endDate: format(new Date(station.endDate), 'dd/MM/yyyy'),
    endTime: format(new Date(station.endTime), 'HH:mm'),
    availability,
    current: station.doctorName || station.coverageResponsibleName || '—',
    action: row.action,
    actionLabel: resolveActionLabel(row.action, row.status),
    actionTone: resolveActionTone(row.action),
    actionNote: resolveActionNote(row),
    value: formatValue(station.value)
  }
}

/** Tabela do backend antigo: mesmo cálculo de hoje, com "Com médico" no lugar de "Ocupada". */
export function formatLegacyAvailability(station: StationFormSchedule): string {
  if (station.hasDoctorConflict) return AVAILABILITY_LABELS.CONFLICT
  return station.isOpen ? AVAILABILITY_LABELS.OPEN : AVAILABILITY_LABELS.DOCTOR
}
